import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';

import { useViolationNotes } from '~/components/compliance/useViolationNotes';
import ViolationNoteSkeleton from '~/components/compliance/ViolationNoteSkeleton';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';

const MAX_NOTE_LENGTH = 500;

/**
 * A violation's notes, written and read in place. The drawer put these behind a modal
 * reached from a Notes icon, so finding out whether anyone had said anything meant
 * leaving the list.
 *
 * The read is gated on the section being open (`enabled`): a car with a dozen violations
 * would otherwise fire a dozen requests to fill sections nobody expanded.
 */
export function ElevatorViolationNotes({
  violationId,
  enabled,
  onNoteAdded,
}: {
  violationId: string;
  enabled: boolean;
  /** Refreshes the violation so its note count follows what was just written. */
  onNoteAdded: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSnackbar } = useLAISnackbar();
  const { notes, isLoading, addNote } = useViolationNotes(violationId, { enabled });

  const submit = async () => {
    const note = draft.trim();
    if (!note) return;

    try {
      setIsSubmitting(true);
      await addNote(note);
      setDraft('');
      onNoteAdded();
      showSnackbar('Note added', { variant: 'success' });
    } catch (_error) {
      showSnackbar('Failed to add note', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap={1.5}>
      {isLoading ? (
        <ViolationNoteSkeleton />
      ) : notes.length > 0 ? (
        <Stack gap={1.5}>
          {notes.map((note) => (
            <Box key={note.id}>
              <Typography variant="caption" color="text.secondary" component="div">
                {note.userEmail} · {format(parseISO(note.createdAt), 'MMM d, yyyy')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {note.note}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No notes on this violation yet.
        </Typography>
      )}

      <Box>
        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          value={draft}
          onChange={(event) => setDraft(event.target.value.slice(0, MAX_NOTE_LENGTH))}
          placeholder="Add a note…"
          slotProps={{ htmlInput: { maxLength: MAX_NOTE_LENGTH } }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Typography variant="caption" color="text.secondary">
            {draft.length}/{MAX_NOTE_LENGTH}
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={submit}
            disabled={!draft.trim() || isSubmitting}
            sx={BUTTON_SX}
          >
            {isSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Add note'}
          </Button>
        </Box>
      </Box>
    </Stack>
  );
}
